/* eslint-disable no-param-reassign */
import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { notification } from 'antd';
import {
    getSchools,
    updateSchool,
    getSingleSchool,
    createSchool,
    getSchoolClassrooms,
    createOrUpdateSchoolClassroom,
    getSchoolClassroomStatistics,
    getSchoolClassroomStatisticsOverall,
    getSchoolContacts,
    createSchoolContact,
    updateSchoolContact,
} from './schoolApi';

import {
    logoutUser,
} from '../user/userApi';

import { SchoolState, SchoolClassroomStatistics } from './types';

const initialState = {
    error: null,
    isFetching: false,
    isLoggedIn: false,
    showTwoFactorPage: false,
    schools: [],
    singleSchool: null,
    isSchoolCreated: false,
    classrooms: [],
    singleClassroom: null,
    classroomStatistics: null,
    overallClassroomStatistics: null,
    schoolClassroomStatistics: null,
    contacts: [],
    isContactsFetching: false,
    singleContact: null,
} as SchoolState;

export const schoolSlice = createSlice({
    name: 'school',
    initialState,
    reducers: {
        loadSingleSchool: (state, action: PayloadAction<number>) => {
            const school = state.schools.find(s => s.id === action.payload) || null;
            state.singleSchool = school;
        },
        resetSchoolCreated: (state) => {
            state.isSchoolCreated = false;
        },
        loadSingleClassroom: (state, action: PayloadAction<number>) => {
            const classroom = state.classrooms.find(c => c.id === action.payload) || null;
            state.singleClassroom = classroom;
        },
        resetSingleClassroom: (state) => {
            state.singleClassroom = null;
        },
        setSelectedSchoolStatistics: (state, action: PayloadAction<SchoolClassroomStatistics | null>) => {
            state.classroomStatistics = action.payload;
        },
        loadSingleContact: (state, action: PayloadAction<number>) => {
            const contact = state.contacts?.find(c => c.id === action.payload) || null;
            state.singleContact = contact;
        },
        resetSingleContact: (state) => {
            state.singleContact = null;
        },
    },
    extraReducers: (builder) => {
        // Update user
        builder
            .addCase(getSchools.fulfilled, (state:SchoolState, action) => {
                state.isFetching = false;
                state.schools = action?.payload?.schools;
                // notification.success({
                //     message: 'Success',
                //     description: 'Duomenys sėkmingai atnaujinti.',
                // });
            });

        // Update school
        builder.addCase(updateSchool.fulfilled, (state: SchoolState, action) => {
            state.isFetching = false;
            const updatedSchool = action?.payload?.school;

            // Update singleSchool
            state.singleSchool = updatedSchool;

            // Update schools array
            const index = state.schools.findIndex(s => s.id === updatedSchool.id);
            if (index !== -1) {
                state.schools[index] = updatedSchool;
            }

            notification.success({
                message: 'Success',
                description: 'Mokykla sėkmingai atnaujinta',
            });
        });

        builder
            .addCase(getSingleSchool.fulfilled, (state:SchoolState, action) => {
                state.isFetching = false;
                state.singleSchool = action?.payload?.school;
            });

        builder
            .addCase(createSchool.fulfilled, (state:SchoolState, action) => {
                state.isFetching = false;
                const newSchool = action?.payload?.school;

                // Add new school to schools array
                if (newSchool) {
                    state.schools.push(newSchool);
                    state.singleSchool = newSchool;
                    state.isSchoolCreated = true;
                }

                notification.success({
                    message: 'Success',
                    description: 'Mokykla sėkmingai sukurta',
                });
            });

        // on logout
        builder
            .addCase(logoutUser.fulfilled, (state:SchoolState) => {
                state.schools = [];
                state.singleSchool = null;
                state.isSchoolCreated = false;
                state.classrooms = [];
                state.singleClassroom = null;
            });

        // Get school classrooms
        builder
            .addCase(getSchoolClassrooms.fulfilled, (state:SchoolState, action) => {
                state.isFetching = false;
                state.classrooms = action?.payload?.classrooms || [];
            });

        // Create or update school classroom
        builder
            .addCase(createOrUpdateSchoolClassroom.fulfilled, (state:SchoolState, action) => {
                state.isFetching = false;
                state.classrooms = action?.payload?.classrooms || [];

                notification.success({
                    message: 'Success',
                    description: 'Klasė sėkmingai išsaugota',
                });
            });

        // Get school classroom statistics
        builder
            .addCase(getSchoolClassroomStatistics.fulfilled, (state:SchoolState, action) => {
                state.isFetching = false;
                state.classroomStatistics = action?.payload?.statistics || [];
            });

        // Get overall school classroom statistics
        builder
            .addCase(getSchoolClassroomStatisticsOverall.fulfilled, (state:SchoolState, action) => {
                state.isFetching = false;
                state.overallClassroomStatistics = action?.payload?.overall_statistics || null;
                state.schoolClassroomStatistics = action?.payload?.school_statistics || [];
            });

        // Get school contacts
        builder.addCase(getSchoolContacts.fulfilled, (state: SchoolState, action) => {
            state.isContactsFetching = false;
            state.contacts = action?.payload?.contacts || [];
        });

        // Create school contact
        builder.addCase(createSchoolContact.fulfilled, (state: SchoolState, action) => {
            state.isContactsFetching = false;
            const newContact = action?.payload?.contact;

            if (newContact && state.contacts) {
                state.contacts.push(newContact);
                state.singleContact = null;
            }

            notification.success({
                message: 'Success',
                description: 'Kontaktas sėkmingai sukurtas',
            });
        });

        // Update school contact
        builder.addCase(updateSchoolContact.fulfilled, (state: SchoolState, action) => {
            state.isContactsFetching = false;
            const updatedContact = action?.payload?.contact;

            if (updatedContact && state.contacts) {
                // Update contacts array
                const index = state.contacts.findIndex(c => c.id === updatedContact.id);
                if (index !== -1) {
                    state.contacts[index] = updatedContact;
                }
                state.singleContact = null;
            }

            notification.success({
                message: 'Success',
                description: 'Kontaktas sėkmingai atnaujintas',
            });
        });

        // Pending state for all requests
        builder.addMatcher(
            isAnyOf(
                getSchools.pending,
                updateSchool.pending,
                getSingleSchool.pending,
                createSchool.pending,
                getSchoolClassrooms.pending,
                createOrUpdateSchoolClassroom.pending,
                getSchoolClassroomStatistics.pending,
                getSchoolClassroomStatisticsOverall.pending,
            ),
            (state:SchoolState) => {
                state.isFetching = true;
                state.error = null;
            },
        );

        builder.addMatcher(
            isAnyOf(
                getSchoolContacts.pending,
                createSchoolContact.pending,
                updateSchoolContact.pending,
            ),
            (state:SchoolState) => {
                state.isContactsFetching = true;
                state.error = null;
            },
        );

        // Error state for all requests
        builder.addMatcher(
            isAnyOf(
                updateSchool.rejected,
                getSchools.rejected,
                getSingleSchool.rejected,
                createSchool.rejected,
                getSchoolClassrooms.rejected,
                createOrUpdateSchoolClassroom.rejected,
                getSchoolClassroomStatistics.rejected,
                getSchoolClassroomStatisticsOverall.rejected,
            ),
            (state:SchoolState, action) => {
                state.isFetching = false;
                state.error = action.error;
            },
        );

        builder.addMatcher(
            isAnyOf(
                getSchoolContacts.rejected,
                createSchoolContact.rejected,
                updateSchoolContact.rejected,
            ),
            (state:SchoolState, action) => {
                state.isContactsFetching = false;
                state.error = action.error;
            },
        );
    },
});

export const { loadSingleSchool, resetSchoolCreated, loadSingleClassroom, resetSingleClassroom, setSelectedSchoolStatistics, loadSingleContact, resetSingleContact } = schoolSlice.actions;
export default schoolSlice.reducer;
